(() => {
  if (customElements.get('shipping-availability')) return;

  // Notify connected blocks when the theme changes the selected variant in the URL.
  const urlChangeEvent = 'sop:variant-url-change';
  ['replaceState', 'pushState'].forEach((method) => {
    const original = history[method];
    history[method] = function (...args) {
      const result = original.apply(this, args);
      window.dispatchEvent(new Event(urlChangeEvent));
      return result;
    };
  });

  class ShippingAvailability extends HTMLElement {
    /**
     * Bind this block to its product form and initialize variant inventory messaging.
     * Each connection owns its listeners, request cache, and pending requests.
     */
    connectedCallback() {
      this.cleanup?.();
      const form = document.getElementById(this.dataset.formId);
      const container = this;

      const shipRow = container.querySelector('.ship-date-row');
      const labelEl = container.querySelector('.ship-date-label');
      const dateTextEl = container.querySelector('.ship-date-text');
      const inventoryContainer = container.querySelector('.inventory-info');
      const metaDataScript = container.querySelector('script[type="application/json"]');
      if (!metaDataScript) return;

      const metaData = JSON.parse(metaDataScript.textContent || '{}');

      // Update these to match Shopify Location names exactly
      const READY_TO_SHIP_LOCATION_NAMES = ['Ecommerce', 'Splash of Pink Westport'];
      const VIRTUAL_WAREHOUSE_NAME = 'Virtual Warehouse';

      const inventoryHtmlCache = new Map();
      const inventoryFlagsCache = new Map(); // { hasReadyToShipStock, virtualQty }
      let lastVariantId = null;
      let inventoryAbortController = null;
      let inventoryReqSeq = 0;

      /**
       * Update the message container's visual and accessibility state.
       * @param {boolean} isVisible Whether the container is visible.
       */
      function setVisible(isVisible) {
        container.classList.toggle('is-visible', isVisible);
        container.setAttribute('aria-hidden', String(!isVisible));
      }

      /**
       * Find the cart line-item property associated with this product form.
       * @returns {HTMLInputElement|null|undefined} The shipping-date property input.
       */
      function getEstimatedShipInput() {
        return form?.querySelector('input[name="properties[Estimated to ship on or by]"]');
      }

      /**
       * Clear and disable the shipping property so a stale date cannot enter the cart.
       */
      function clearEstimatedShipProperty() {
        const hiddenInput = getEstimatedShipInput();
        if (!hiddenInput) return;
        hiddenInput.value = '';
        hiddenInput.disabled = true;
      }

      /**
       * Store a nonempty display value in the cart shipping property.
       * @param {string} value Formatted date or overdue message; an empty value disables the input.
       */
      function setEstimatedShipProperty(value) {
        const hiddenInput = getEstimatedShipInput();
        if (!hiddenInput) return;

        const normalizedValue = String(value || '').trim();
        hiddenInput.value = normalizedValue;
        hiddenInput.disabled = normalizedValue === '';
      }

      /**
       * Read the shipping-date metafields rendered for a variant.
       * @param {string} variantId Shopify variant identifier.
       * @returns {Object} Date metafields, or an empty object when unavailable.
       */
      function getVariantDateData(variantId) {
        return metaData?.variants?.[variantId] || {};
      }

      /**
       * Parse the leading YYYY-MM-DD portion of a metafield at noon UTC.
       * @param {string|null} dateString Raw date metafield value.
       * @returns {Date|null} Parsed date, or null when required parts are missing.
       */
      function parseDateStringToUTC(dateString) {
        if (!dateString) return null;

        const normalized = String(dateString).slice(0, 10);
        const parts = normalized.split('-');
        if (parts.length !== 3) return null;

        const year = Number(parts[0]);
        const month = Number(parts[1]);
        const day = Number(parts[2]);

        if (!year || !month || !day) return null;

        return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
      }

      /**
       * Add calendar days in UTC without mutating the supplied date.
       * @param {Date|null} date Base date.
       * @param {number} days Number of days to add.
       * @returns {Date|null} Adjusted date, or null when no base date is available.
       */
      function addDaysUTC(date, days) {
        if (!date) return null;
        const clone = new Date(date.getTime());
        clone.setUTCDate(clone.getUTCDate() + days);
        return clone;
      }

      /**
       * Apply the five-day shipping buffer to a variant's earliest ship date.
       * @param {string} variantId Shopify variant identifier.
       * @returns {Date|null} Buffered date, or null when unavailable.
       */
      function getEarliestShipDisplayDate(variantId) {
        const variantDates = getVariantDateData(variantId);
        const baseDate = parseDateStringToUTC(variantDates?.earliest_ship_date);
        if (!baseDate) return null;
        return addDaysUTC(baseDate, 5);
      }

      /**
       * Read the delayed-arrival date, which is already a final display date.
       * @param {string} variantId Shopify variant identifier.
       * @returns {Date|null} Delayed-arrival date without an additional buffer.
       */
      function getDelayedArrivalDisplayDate(variantId) {
        const variantDates = getVariantDateData(variantId);
        return parseDateStringToUTC(variantDates?.delayed_arrival_style_date);
      }

      /**
       * Choose the later of the buffered earliest date and delayed-arrival date.
       * @param {string} variantId Shopify variant identifier.
       * @returns {Date|null} Applicable date, or null if neither metafield provides one.
       */
      function getWinningShipDate(variantId) {
        const earliestDisplayDate = getEarliestShipDisplayDate(variantId);
        const delayedDisplayDate = getDelayedArrivalDisplayDate(variantId);

        if (earliestDisplayDate && delayedDisplayDate) {
          return delayedDisplayDate > earliestDisplayDate ? delayedDisplayDate : earliestDisplayDate;
        }

        return delayedDisplayDate || earliestDisplayDate || null;
      }

      /**
       * Format a UTC date for the storefront and shipping cart property.
       * @param {Date|null} date Date to display.
       * @returns {string} Short month and day, or an empty string.
       */
      function formatDateObject(date) {
        if (!date) return '';
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[date.getUTCMonth()]} ${date.getUTCDate()}`;
      }

      /**
       * Summarize available inventory at Ecommerce, Westport, and Virtual Warehouse.
       * Closed or unrecognized locations do not count as ready-to-ship stock.
       * @param {Array<Object>} edges Inventory-level edges from the inventory service.
       * @returns {{hasReadyToShipStock: boolean, virtualQty: number}} Messaging eligibility inputs.
       */
      function computeInventoryFlags(edges) {
        let hasReadyToShipStock = false;
        let virtualQty = 0;

        (edges || []).forEach((edge) => {
          const locationName = edge?.node?.location?.name || '';
          const available = edge?.node?.quantities?.find((q) => q.name === 'available');
          const qty = available ? Number(available.quantity || 0) : 0;

          if (READY_TO_SHIP_LOCATION_NAMES.includes(locationName) && qty > 0) {
            hasReadyToShipStock = true;
          }

          if (locationName === VIRTUAL_WAREHOUSE_NAME) {
            virtualQty += qty;
          }
        });

        return { hasReadyToShipStock, virtualQty };
      }

      /**
       * Require positive virtual stock and no available ready-to-ship stock.
       * @param {{hasReadyToShipStock: boolean, virtualQty: number}|undefined} flags Inventory summary.
       * @returns {boolean} Whether shipping-date messaging applies.
       */
      function shouldShowShipMessaging(flags) {
        // Show ship messaging ONLY when:
        // - no available stock in ready-to-ship locations
        // - virtual warehouse has qty (>0)
        return !!flags && flags.hasReadyToShipStock === false && flags.virtualQty > 0;
      }

      /**
       * Render the winning date or overdue message and update the cart property.
       * Hide the row and clear the property when no usable date exists.
       * @param {string} variantId Shopify variant identifier.
       */
      function renderShipMessaging(variantId) {
        const winningShipDate = getWinningShipDate(variantId);

        // If no usable date, hide row
        if (!winningShipDate) {
          shipRow.style.display = 'none';
          clearEstimatedShipProperty();
          return;
        }

        const now = new Date();
        const today = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0));

        if (today > winningShipDate) {
          labelEl.textContent = 'Due back in stock soon';
          dateTextEl.textContent = '';
          setEstimatedShipProperty('Due back in stock soon');
        } else {
          const formatted = formatDateObject(winningShipDate);
          labelEl.textContent = 'Estimated to ship on or by';
          dateTextEl.textContent = formatted;
          setEstimatedShipProperty(formatted);
        }

        shipRow.style.display = 'block';
      }

      /**
       * Use cached inventory to render or clear messaging for the active variant.
       * @param {string} variantId Shopify variant identifier.
       */
      function applyShipRowVisibility(variantId) {
        const flags = inventoryFlagsCache.get(variantId);

        // If we don't have inventory yet, hide the ship row (prevents flicker)
        if (!flags) {
          shipRow.style.display = 'none';
          clearEstimatedShipProperty();
          return;
        }

        if (shouldShowShipMessaging(flags)) {
          renderShipMessaging(variantId);
        } else {
          // Not on-order scenario => hide ship messaging
          shipRow.style.display = 'none';
          clearEstimatedShipProperty();
        }
      }

      /**
       * Fetch or reuse inventory, then apply the variant's shipping message.
       * Abort superseded requests and ignore stale responses; failures hide the message.
       * @param {string} variantId Shopify variant identifier.
       * @returns {Promise<void>} Resolves after cached or fetched inventory is applied.
       */
      async function fetchAndDisplayInventory(variantId) {
        if (inventoryAbortController) inventoryAbortController.abort();
        inventoryAbortController = new AbortController();
        const reqId = ++inventoryReqSeq;

        inventoryContainer.textContent = 'Loading inventory…';

        // Cache hit
        if (inventoryHtmlCache.has(variantId) && inventoryFlagsCache.has(variantId)) {
          inventoryContainer.innerHTML = inventoryHtmlCache.get(variantId);
          applyShipRowVisibility(variantId);
          return;
        }

        try {
          const response = await fetch(`https://sop-inventories.vercel.app/inventory/${variantId}`, {
            signal: inventoryAbortController.signal,
            headers: { 'ngrok-skip-browser-warning': 'true' },
          });

          if (!response.ok) throw new Error('Network response was not ok');
          const data = await response.json();

          if (reqId !== inventoryReqSeq) return;

          const edges = data?.inventoryItem?.inventoryLevels?.edges || [];
          const flags = computeInventoryFlags(edges);

          let html = 'Inventory info not available';
          if (Array.isArray(edges) && edges.length) {
            html = '<ul class="inventory-locations">';
            edges.forEach((edge) => {
              const locationName = edge?.node?.location?.name || 'Unknown';
              const available = edge?.node?.quantities?.find((q) => q.name === 'available');
              const quantity = available ? available.quantity : 0;
              html += `<li><strong>${locationName}:</strong> ${quantity}</li>`;
            });
            html += '</ul>';
          }

          inventoryHtmlCache.set(variantId, html);
          inventoryFlagsCache.set(variantId, flags);

          inventoryContainer.innerHTML = html;

          // Now decide whether to show ship message based on inventory
          applyShipRowVisibility(variantId);
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.error('Error fetching inventory:', error);
          inventoryContainer.textContent = '';
          shipRow.style.display = 'none';
          clearEstimatedShipProperty();
        }
      }

      /**
       * Switch variants, clear stale messaging, and load the new inventory.
       * Repeated updates for the same variant are ignored.
       * @param {string} variantId Shopify variant identifier.
       */
      function updateVariant(variantId) {
        if (!variantId) {
          lastVariantId = null;
          inventoryReqSeq++;
          inventoryAbortController?.abort();
          shipRow.style.display = 'none';
          clearEstimatedShipProperty();
          setVisible(false);
          return;
        }
        if (variantId === lastVariantId) return;
        lastVariantId = variantId;

        setVisible(true);

        // Hide ship row until inventory flags are known
        shipRow.style.display = 'none';
        clearEstimatedShipProperty();

        // Always fetch inventory (or use cache)
        fetchAndDisplayInventory(variantId);
      }

      let urlTimer = null;
      /**
       * Debounce variant updates after URL changes, falling back to the product form.
       */
      function checkUrlForVariantDebounced() {
        clearTimeout(urlTimer);
        urlTimer = setTimeout(() => {
          const urlParams = new URLSearchParams(window.location.search);
          const variantId = container.dataset.variantSource === 'form'
            ? variantInput?.value
            : urlParams.get('variant') || variantInput?.value;
          if (variantId) updateVariant(variantId);
        }, 50);
      }

      const variantInput = form?.querySelector('input[name="id"]');
      const onVariantChange = (event) => updateVariant(event.target.value);
      window.addEventListener('popstate', checkUrlForVariantDebounced);
      window.addEventListener(urlChangeEvent, checkUrlForVariantDebounced);
      variantInput?.addEventListener('change', onVariantChange);

      /** Release the resources owned by this connection before removal or reconnection. */
      this.cleanup = () => {
        clearTimeout(urlTimer);
        inventoryReqSeq++;
        inventoryAbortController?.abort();
        window.removeEventListener('popstate', checkUrlForVariantDebounced);
        window.removeEventListener(urlChangeEvent, checkUrlForVariantDebounced);
        variantInput?.removeEventListener('change', onVariantChange);
        clearEstimatedShipProperty();
      };

      checkUrlForVariantDebounced();

    }

    /**
     * Cancel this block's pending work and remove listeners and cart properties.
     */
    disconnectedCallback() {
      this.cleanup?.();
      this.cleanup = null;
    }
  }

  customElements.define('shipping-availability', ShippingAvailability);
})();
